/**
 * L2: Items tab view-model (01-references §6 S3). Pure functions over the snapshot. The view only
 * turns the model into DOM.
 */
import {
  CATEGORIES,
  CATEGORY_LABEL,
  type CategoryId,
  type Route,
  type SortKey,
  type VaultItem,
  type VaultSnapshot,
  type WalletFailure,
  type WalletId,
} from "../contract.js";

export type ListId = NonNullable<Route["list"]>;

const PLURAL: Record<CategoryId, string> = {
  login: "Logins",
  password: "Passwords",
  "secure-note": "Secure Notes",
  "credit-card": "Credit Cards",
  identity: "Identities",
  "api-credential": "API Credentials",
  database: "Databases",
  server: "Servers",
};

export function listTitle(list: ListId): string {
  switch (list) {
    case "all":
      return "All Items";
    case "favorites":
      return "Favorites";
    case "archive":
      return "Archive";
    case "deleted":
      return "Recently Deleted";
    default:
      return PLURAL[list];
  }
}

export function isFavorite(item: VaultItem, overrides: Record<string, boolean>): boolean {
  return overrides[item.id] ?? item.favorite;
}

const live = (i: VaultItem) => !i.archived && !i.deleted;

export function visibleItems(
  snapshot: VaultSnapshot,
  scope: WalletId | null,
  list: ListId,
  favoriteOverrides: Record<string, boolean>,
): VaultItem[] {
  const inScope = snapshot.items.filter((i) => scope === null || i.wallet === scope);
  switch (list) {
    case "all":
      return inScope.filter(live);
    case "favorites":
      return inScope.filter((i) => live(i) && isFavorite(i, favoriteOverrides));
    case "archive":
      return inScope.filter((i) => i.archived && !i.deleted);
    case "deleted":
      return inScope.filter((i) => i.deleted);
    default:
      return inScope.filter((i) => live(i) && i.category === list);
  }
}

const byTitle = (a: VaultItem, b: VaultItem) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

export function sortItems(items: VaultItem[], sort: SortKey): VaultItem[] {
  const out = [...items];
  if (sort === "title") out.sort(byTitle);
  else if (sort === "modified") out.sort((a, b) => (a.modifiedAt < b.modifiedAt ? 1 : a.modifiedAt > b.modifiedAt ? -1 : byTitle(a, b)));
  else out.sort((a, b) => b.useCount - a.useCount || byTitle(a, b));
  return out;
}

export interface Section {
  /** A-Z letter, "#" for anything else, or null when the sort has no sections. */
  header: string | null;
  items: VaultItem[];
}

/** Title sort gets iOS-style letter sections; the other sorts are one flat list. */
export function sectionize(sorted: VaultItem[], sort: SortKey): Section[] {
  if (sorted.length === 0) return [];
  if (sort !== "title") return [{ header: null, items: sorted }];
  const out: Section[] = [];
  for (const item of sorted) {
    const c = item.title.trim()[0]?.toUpperCase() ?? "#";
    const header = c >= "A" && c <= "Z" ? c : "#";
    const last = out[out.length - 1];
    if (last && last.header === header) last.items.push(item);
    else out.push({ header, items: [item] });
  }
  // "#" sorts last, as in the iOS index.
  return [...out.filter((s) => s.header !== "#"), ...out.filter((s) => s.header === "#")];
}

export interface ItemsModel {
  title: string;
  list: ListId;
  count: number;
  sections: Section[];
  empty: boolean;
  categoryCounts: { category: CategoryId; label: string; count: number }[];
  allCount: number;
  archiveCount: number;
  deletedCount: number;
  failed: WalletFailure[];
}

export function itemsModel(
  snapshot: VaultSnapshot,
  opts: { scope: WalletId | null; list: ListId; sort: SortKey; favoriteOverrides: Record<string, boolean> },
): ItemsModel {
  const rows = sortItems(visibleItems(snapshot, opts.scope, opts.list, opts.favoriteOverrides), opts.sort);
  const inScope = snapshot.items.filter((i) => opts.scope === null || i.wallet === opts.scope);
  return {
    title: listTitle(opts.list),
    list: opts.list,
    count: rows.length,
    sections: sectionize(rows, opts.sort),
    empty: rows.length === 0,
    categoryCounts: CATEGORIES.map((c) => ({
      category: c,
      label: CATEGORY_LABEL[c],
      count: inScope.filter((i) => live(i) && i.category === c).length,
    })),
    allCount: inScope.filter(live).length,
    archiveCount: inScope.filter((i) => i.archived && !i.deleted).length,
    deletedCount: inScope.filter((i) => i.deleted).length,
    failed: snapshot.failedWallets.filter((f) => opts.scope === null || f.wallet === opts.scope),
  };
}

/** The one-line sub-title under a row: username, else the website host, else the category. */
export function rowSubtitle(item: VaultItem): string {
  const user = item.fields.find((f) => f.kind === "username");
  if (user) return user.value;
  const url = item.fields.find((f) => f.kind === "url");
  if (url) {
    try {
      return new URL(url.value).hostname;
    } catch {
      return url.value;
    }
  }
  return CATEGORY_LABEL[item.category];
}
